import {
  GuaranteeSecurityType,
  GuaranteesAccountType,
  type TGuaranteesAccount,
} from '~/store/guarantees/types.ts';
import {
  GuaranteesAccountTargetField,
  type TGuaranteesAccountOption,
} from './types.ts';

export const normalizeGuaranteesIban = (value: string): string =>
  value.replace(/\s/g, '').toUpperCase();

export const isGuaranteesOurBankIban = (value: string): boolean =>
  /^UA\d{2}322313[A-Z0-9]+$/.test(normalizeGuaranteesIban(value));

export const isGuaranteesPrincipalDebitAccountIban = (value: string): boolean =>
  /^UA\d{2}3223130{5}(?:2600|2650|25)[A-Z0-9]+$/.test(normalizeGuaranteesIban(value));

export const getGuaranteesAccountFieldBySecurityType = (
  securityType: GuaranteeSecurityType.COVERAGE_ACCOUNT | GuaranteeSecurityType.DEPOSIT_RIGHTS | null
): GuaranteesAccountTargetField | null => {
  if (securityType === GuaranteeSecurityType.DEPOSIT_RIGHTS) {
    return GuaranteesAccountTargetField.DepositAccountIban;
  }
  if (securityType === GuaranteeSecurityType.COVERAGE_ACCOUNT) {
    return GuaranteesAccountTargetField.CoverageAccountIban;
  }
  return null;
};

const buildAccountOption = (
  iban: string,
  targetField: GuaranteesAccountTargetField,
  accountType = '',
  frozen: boolean | null = null,
  ourBank: boolean | null = null
): TGuaranteesAccountOption | null => {
  const normalizedIban = normalizeGuaranteesIban(iban);

  if (!normalizedIban) {
    return null;
  }

  return {
    value: `${targetField}:${normalizedIban}`,
    label: normalizedIban,
    iban: normalizedIban,
    targetField,
    accountType,
    frozen,
    ourBank,
  };
};

const getGuaranteesAccountTargetField = (
  accountType?: string | null
): GuaranteesAccountTargetField => {
  const normalizedType = String(accountType ?? '').trim().toUpperCase();

  return normalizedType === GuaranteesAccountType.DEPOSIT
    ? GuaranteesAccountTargetField.DepositAccountIban
    : GuaranteesAccountTargetField.CoverageAccountIban;
};

export const getGuaranteesCounterpartyAccountOptions = (
  accounts: TGuaranteesAccount[] = []
): TGuaranteesAccountOption[] => {
  const seenIbans = new Set<string>();

  return accounts
    .map((account) =>
      buildAccountOption(
        String(account.iban ?? ''),
        getGuaranteesAccountTargetField(account.type),
        String(account.type ?? '').trim(),
        typeof account.frozen === 'boolean' ? account.frozen : null,
        typeof account.ourBank === 'boolean' ? account.ourBank : null
      )
    )
    .filter((option): option is TGuaranteesAccountOption => {
      if (!option) {
        return false;
      }

      const key = option.iban.toUpperCase();

      if (seenIbans.has(key)) {
        return false;
      }
      seenIbans.add(key);
      return true;
    });
};

export const filterGuaranteesAccountOptionsBySecurityType = (
  accountOptions: TGuaranteesAccountOption[],
  securityType: GuaranteeSecurityType.COVERAGE_ACCOUNT | GuaranteeSecurityType.DEPOSIT_RIGHTS | null
): TGuaranteesAccountOption[] => {
  const targetField = getGuaranteesAccountFieldBySecurityType(securityType);

  return targetField
    ? accountOptions.filter((option) => option.targetField === targetField)
    : [];
};

export const getGuaranteesPrincipalDebitAccountOptions = (
  accounts: TGuaranteesAccount[] = []
): TGuaranteesAccountOption[] => {
  const seenIbans = new Set<string>();

  return accounts
    .map((account) =>
      buildAccountOption(
        String(account.iban ?? ''),
        GuaranteesAccountTargetField.PrincipalDebitAccountIban,
        String(account.type ?? '').trim(),
        typeof account.frozen === 'boolean' ? account.frozen : null,
        typeof account.ourBank === 'boolean' ? account.ourBank : null
      )
    )
    .filter((option): option is TGuaranteesAccountOption => {
      if (!option) {
        return false;
      }

      const key = option.iban.toUpperCase();

      if (seenIbans.has(key)) {
        return false;
      }
      seenIbans.add(key);
      return true;
    });
};
